/**
 * <TutorCloseOutPage> — le bilan de séance du masteur, après le live (page).
 *
 * L'écran d'aujourd'hui (`end-session-tutor`, `CloseOutScreen`) rhabillé, à parité — le nouveau
 * bilan est un autre chantier. De haut en bas :
 *  - Zoé, « Bilan de la séance » et « Live du MARDI 27 OCTOBRE 17:00 » ;
 *  - « Les bases » : une carte par axe (`<TutorQualityAxisCard>`). Quand personne n'est venu,
 *    l'encadré « Personne n'est venu à cette séance » — avec le temps resté — et seuls les axes qui
 *    ont encore une alerte (l'écran les filtre) ;
 *  - « Tes élèves (1/2) » : une carte par élève (`<TutorCloseOutStudentCard>`), absent compris ;
 *    « Aucun élève sur ce live. » sans élève ;
 *  - « Côté plateforme » (`<TutorPlatformFeedbackCard>`) ;
 *  - au pied : « Terminer la session » quand tout est fait (ou qu'aucun élève n'est venu), sinon
 *    « Il te reste 2 comptes-rendus » et « Plus tard », qui demande confirmation
 *    (« Tu n'as pas tout complété »).
 *
 * Un vocal s'enregistre sur place, dans la carte qui le demande (`voiceRecorder`, un seul à la
 * fois) : plus de feuille par-dessus la page (Wajih, 03/11).
 * Contrôlée : tout l'état métier arrive en props, chaque geste remonte avec l'élève qu'il vise.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChampionFigure } from "../../atoms/ChampionFigure"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { AlertCard } from "../../molecules/AlertCard"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { TutorQualityAxisCard } from "../../molecules/TutorQualityAxisCard"
import { type InlineVoiceRecorderProps } from "../../organisms/InlineVoiceRecorder"
import { isPresent, TutorCloseOutStudentCard } from "../../organisms/TutorCloseOutStudentCard"
import {
    TutorPlatformFeedbackCard,
    type TutorPlatformFeedbackCardProps,
} from "../../organisms/TutorPlatformFeedbackCard"
import { PageTemplate } from "../../templates/PageTemplate"
import {
    type UITutorCloseOutStudent,
    type UITutorCommentMode,
    type UITutorPlatformFeedback,
    type UITutorQualityAxis,
} from "../../../ports/primary/ui/types/tutorCloseOut"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { TutorCloseOutSkeleton } from "./TutorCloseOutSkeleton"

/** La colonne de l'écran d'aujourd'hui : 560 px au-delà du téléphone. */
const CLOSE_OUT_WIDTH = 560

type StudentId = UITutorCloseOutStudent["id"]

/** Les gestes d'une carte d'élève, chacun avec l'élève qu'il vise. */
export type TutorCloseOutStudentEvents = {
    onRateStudent: (id: StudentId, rating: number) => void
    onToggleStudentTag: (id: StudentId, tag: string) => void
    onChangeStudentCommentMode: (id: StudentId, mode: UITutorCommentMode) => void
    onChangeStudentCommentText: (id: StudentId, text: string) => void
    onRecordStudentVoice: (id: StudentId) => void
    onDeleteStudentVoice: (id: StudentId) => void
    onToggleStudentVoicePlay?: (id: StudentId) => void
    onRetryStudentComment: (id: StudentId) => void
    onStudentDone: (id: StudentId) => void
}

/** Les gestes de « Côté plateforme ». */
export type TutorCloseOutPlatformEvents = {
    onRatePlatform: TutorPlatformFeedbackCardProps["onRate"]
    onTogglePlatformTag: TutorPlatformFeedbackCardProps["onToggleTag"]
    onChangePlatformCommentMode: TutorPlatformFeedbackCardProps["onChangeMode"]
    onChangePlatformCommentText: TutorPlatformFeedbackCardProps["onChangeText"]
    onRecordPlatformVoice: TutorPlatformFeedbackCardProps["onRecordVoice"]
    onDeletePlatformVoice: TutorPlatformFeedbackCardProps["onDeleteVoice"]
    onRetryPlatformComment: TutorPlatformFeedbackCardProps["onRetry"]
    /** Réécouter le ressenti en vocal. Absent : le vocal se montre sans lecture. */
    onTogglePlatformVoicePlay?: TutorPlatformFeedbackCardProps["onToggleVoicePlay"]
}

export type TutorCloseOutPageProps = TutorCloseOutStudentEvents &
    TutorCloseOutPlatformEvents & {
        /** « MARDI 27 OCTOBRE 17:00 » — absent tant que l'occurrence n'est pas lue. */
        dateLabel?: string
        /** La clôture qualité se lit : la page montre sa forme. */
        loading?: boolean
        error?: unknown
        onRetry?: () => void
        /** Les axes de « Les bases » à montrer — sans élève, l'écran n'y garde que ceux qui ont une alerte. */
        proxies: UITutorQualityAxis[]
        /** Personne n'est venu : l'encadré, et le temps resté (0 : la phrase courte). */
        noStudentAttended?: { tutorConnectionMinutes: number } | null
        students: UITutorCloseOutStudent[]
        /** L'enregistrement en cours, et la carte où il court. Absent : aucun. */
        voiceRecorder?: (InlineVoiceRecorderProps & { targetId: StudentId | "platform" }) | null
        /** Le vocal en cours de réécoute. */
        playingVoiceId?: StudentId | "platform" | null
        /** Où en est sa lecture, de 0 à 1. */
        playingVoiceProgress?: number
        platform: UITutorPlatformFeedback
        /** « Terminer la session ». */
        onFinish: () => void
        /** « Plus tard » : l'écran ouvre la confirmation. */
        onLater: () => void
        /** La confirmation de « Plus tard » est ouverte. */
        laterConfirmVisible?: boolean
        /** « Continuer plus tard » : on part, les bilans restent à finir depuis l'accueil. */
        onConfirmLater: () => void
        /** « Revenir » : on reste. */
        onCancelLater: () => void
        testID?: string
    }

export function TutorCloseOutPage(props: TutorCloseOutPageProps) {
    const {
        dateLabel,
        loading = false,
        error,
        onRetry,
        proxies,
        noStudentAttended,
        students,
        playingVoiceId,
        playingVoiceProgress = 0,
        voiceRecorder,
        platform,
        onFinish,
        onLater,
        laterConfirmVisible = false,
        onConfirmLater,
        onCancelLater,
        testID,
    } = props

    const present = students.filter(isPresent)
    const doneCount = present.filter((student) => student.done).length
    const remaining = present.length - doneCount
    const canFinish = present.length === 0 || remaining === 0
    const remainingLabel = countLabel(remaining, "compte-rendu", "comptes-rendus")

    // Un vocal qui s'enregistre ne se perd pas en partant : la sortie attend qu'il soit fini ou abandonné.
    const recording = voiceRecorder != null
    // En erreur, rien n'est lu : « Terminer la session » ne terminerait rien. La sortie seule reste, celle de
    // « Plus tard » sans sa confirmation (il n'y a rien à perdre) — sinon la page n'a aucune sortie sur iOS.
    const failed = error != null && !loading
    const footer = failed ? (
        <Stack align="center">
            <TextButton label="Continuer plus tard" color="text-muted" onPress={onConfirmLater} />
        </Stack>
    ) : canFinish ? (
        <Stack gap="1" align="center">
            <Button
                label="Terminer la session"
                iconName="check"
                color="success"
                onPress={onFinish}
                disabled={recording}
                fullWidth
            />
            {recording && (
                <Text size="text-sm" color="text-muted" center>
                    Termine ou annule ton vocal d'abord.
                </Text>
            )}
        </Stack>
    ) : (
        <Stack gap="1" align="center">
            <Text size="text-sm" color="text-muted" center>
                {`Il te reste ${remainingLabel}`}
            </Text>
            <TextButton label="Plus tard" color="text-muted" onPress={onLater} disabled={recording} />
        </Stack>
    )

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            skeleton={<TutorCloseOutSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={CLOSE_OUT_WIDTH}
            footer={loading ? undefined : footer}
        >
            <Stack gap="6">
                <Stack gap="2" align="center">
                    <ChampionFigure name="zoe" height={86} />
                    <Text size="text-2xl" weight="bold" color="primary" center>
                        Bilan de la séance
                    </Text>
                    {dateLabel != null && (
                        <Text size="text-sm" color="text-muted" center>
                            {`Live du ${dateLabel}`}
                        </Text>
                    )}
                </Stack>

                <Stack gap="3">
                    <SectionLabel>Les bases</SectionLabel>
                    {noStudentAttended != null && (
                        <AlertCard
                            severity="info"
                            title="Personne n'est venu à cette séance"
                            message={
                                noStudentAttended.tutorConnectionMinutes > 0
                                    ? `Tu es resté ${noStudentAttended.tutorConnectionMinutes} min. Il n'y a pas de bilan qualité à faire.`
                                    : "Il n'y a pas de bilan qualité à faire."
                            }
                        />
                    )}
                    {proxies.map((axis) => (
                        <TutorQualityAxisCard key={axis.name} axis={axis} />
                    ))}
                </Stack>

                <Stack gap="3">
                    <SectionLabel>
                        {present.length > 0 ? `Tes élèves (${doneCount}/${present.length})` : "Tes élèves"}
                    </SectionLabel>
                    {students.length === 0 ? (
                        <Text color="text-muted" center style={styles.empty}>
                            Aucun élève sur ce live.
                        </Text>
                    ) : (
                        students.map((student) => (
                            <TutorCloseOutStudentCard
                                key={student.id}
                                student={student}
                                onRate={(rating) => props.onRateStudent(student.id, rating)}
                                onToggleTag={(tag) => props.onToggleStudentTag(student.id, tag)}
                                onChangeMode={(mode) => props.onChangeStudentCommentMode(student.id, mode)}
                                onChangeText={(text) => props.onChangeStudentCommentText(student.id, text)}
                                onRecordVoice={() => props.onRecordStudentVoice(student.id)}
                                recorder={
                                    voiceRecorder != null && String(voiceRecorder.targetId) === String(student.id)
                                        ? voiceRecorder
                                        : null
                                }
                                onDeleteVoice={() => props.onDeleteStudentVoice(student.id)}
                                onToggleVoicePlay={
                                    props.onToggleStudentVoicePlay != null
                                        ? () => props.onToggleStudentVoicePlay?.(student.id)
                                        : undefined
                                }
                                voicePlaying={playingVoiceId === student.id}
                                voiceProgress={playingVoiceId === student.id ? playingVoiceProgress : 0}
                                onRetry={() => props.onRetryStudentComment(student.id)}
                                onDone={() => props.onStudentDone(student.id)}
                            />
                        ))
                    )}
                </Stack>

                <Stack gap="3">
                    <SectionLabel>Côté plateforme</SectionLabel>
                    <TutorPlatformFeedbackCard
                        feedback={platform}
                        onRate={props.onRatePlatform}
                        onToggleTag={props.onTogglePlatformTag}
                        onChangeMode={props.onChangePlatformCommentMode}
                        onChangeText={props.onChangePlatformCommentText}
                        onRecordVoice={props.onRecordPlatformVoice}
                        recorder={voiceRecorder?.targetId === "platform" ? voiceRecorder : null}
                        onDeleteVoice={props.onDeletePlatformVoice}
                        onToggleVoicePlay={props.onTogglePlatformVoicePlay}
                        voicePlaying={playingVoiceId === "platform"}
                        voiceProgress={playingVoiceId === "platform" ? playingVoiceProgress : 0}
                        onRetry={props.onRetryPlatformComment}
                    />
                </Stack>
            </Stack>

            <ConfirmDialog
                visible={laterConfirmVisible}
                title="Tu n'as pas tout complété"
                message={`Il te reste ${remainingLabel} à finaliser. Les parents recevront un rapport incomplet si la session est finalisée maintenant. Tu peux y revenir depuis l'accueil.`}
                confirmLabel="Revenir"
                danger={false}
                onConfirm={onCancelLater}
                cancelLabel="Continuer plus tard"
                onCancel={onConfirmLater}
                onDismiss={onCancelLater}
            />
        </PageTemplate>
    )
}

const styles = StyleSheet.create((theme) => ({
    empty: {
        paddingVertical: theme.spacing["5"],
    },
}))
